import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faStore,
  faUserTie,
  faUsers,
  faMoneyBillWave,
  faChartLine,
  faBoxes,
  faChartBar,
  faCreditCard,
  faCalculator,
  faCog,
} from "@fortawesome/free-solid-svg-icons";

import ShopInfoTab from "./shop/ShopInfoTab";
import OwnersTab from "./shop/OwnersTab";
import SellersTab from "./shop/SellersTab";
import CapitalTab from "./shop/CapitalTab";
import SalesAnalysisTab from "./shop/SalesAnalysisTab";
import StockTab from "./shop/StockTab";
import OverallSalesTab from "./shop/OverallSalesTab";
import PaymentTab from "./shop/PaymentTab";
import NotificationSettingsTab from "./shop/NotificationSettingsTab";
import { useAuth, useShop } from "../../hooks";
import { useTransactions } from "../../hooks/useTransactions";
import { toastError, toastSuccess } from "../../utils/toast";

import EditOwnerModal from "./shop/EditOwnerModal";
import ViewOwnerModal from "./shop/ViewOwnerModal";
import EditSellerModal from "./shop/EditSellerModal";
import ViewSellerModal from "./shop/ViewSellerModal";
import AddCapitalModal from "./shop/AddCapitalModal";
import EditCapitalModal from "./shop/EditCapitalModal";
import AddSaasPaymentModal from "./shop/AddSaasPaymentModal";
import ViewSaasPaymentModal from "./shop/ViewSaasPaymentModal";
import SalesTab from "./shop/SalesTab";
import FinancialTab from "../shopOwner/shop/FinancialTab";
import SalesCloseoutTab from "../shopOwner/shop/SalesCloseoutTab";

const statusStyles = {
  ACTIVE: "bg-green-50 text-green-700 border-green-200",
  INACTIVE: "bg-red-50 text-red-700 border-red-200",
};

const ShopDetailView = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const {
    shop: responseShop,
    loading,
    error,
    refresh,
    updateShop,
  } = useShop(shopId);

  const { transactions } = useTransactions(shopId, { per_page: 1000 });

  const shop = responseShop && {
    ...responseShop,
    shopId: responseShop.shopId ?? responseShop.id ?? shopId,
    name: responseShop.name ?? responseShop.shop_name,
    type: responseShop.type ?? responseShop.shop_type,
    owners: responseShop.owners ?? [],
    sellers: responseShop.sellers ?? [],
    stock: responseShop.stock ?? [],
  };
  const onBack = () => navigate("/app/shops");
  const showToast = toastSuccess;
  const onUpdateShop = async (updatedShop) => {
    try {
      await updateShop(updatedShop);
      toastSuccess("Shop updated successfully");
      await refresh();
    } catch (updateError) {
      toastError(updateError?.message || "Failed to update shop");
    }
  };

  const [showAddCapital, setShowAddCapital] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);

  const [viewOwner, setViewOwner] = useState(null);
  const [editOwner, setEditOwner] = useState(null);
  const [viewSeller, setViewSeller] = useState(null);
  const [editSeller, setEditSeller] = useState(null);
  const [editCapital, setEditCapital] = useState(null);
  const [viewPayment, setViewPayment] = useState(null);

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-sm text-gray-600">
        Loading shop details...
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-sm text-red-600">Failed to load shop details.</p>
        <Button
          onClick={() => refresh()}
          className="bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  const handleUpdateOwner = (updated) => {
    onUpdateShop({
      ...shop,
      owners: shop.owners.map((o) => (o.id === updated.id ? updated : o)),
    });
    showToast("Owner updated successfully");
    setEditOwner(null);
  };

  const handleRemoveOwner = (id) => {
    onUpdateShop({ ...shop, owners: shop.owners.filter((o) => o.id !== id) });
  };

  const handleUpdateSeller = (updated) => {
    onUpdateShop({
      ...shop,
      sellers: shop.sellers.map((s) => (s.id === updated.id ? updated : s)),
    });
    showToast("Seller updated successfully");
    setEditSeller(null);
  };

  const handleRemoveSeller = (id) => {
    onUpdateShop({
      ...shop,
      sellers: shop.sellers.filter((s) => s.id !== id),
    });
  };

  const handleAddCapital = (entry) => {
    onUpdateShop({
      ...shop,
      capital: shop.capital + entry.capital_amount,
      capitalHistory: [...(shop.capitalHistory || []), entry],
    });
    showToast("Capital added successfully");
    setShowAddCapital(false);
  };

  const handleEditCapital = (updatedEntry) => {
    const oldEntry = shop.capitalHistory?.find((h) => h.id === updatedEntry.id);
    const diff = updatedEntry.capital_amount - (oldEntry?.capital_amount || 0);
    onUpdateShop({
      ...shop,
      capital: shop.capital + diff,
      capitalHistory: shop.capitalHistory?.map((h) =>
        h.id === updatedEntry.id ? updatedEntry : h,
      ),
    });
    showToast("Capital updated successfully");
    setEditCapital(null);
  };

  const handleDeleteCapital = (id) => {
    const entry = shop.capitalHistory?.find((h) => h.id === id);
    onUpdateShop({
      ...shop,
      capital: shop.capital - (entry?.capital_amount || 0),
      capitalHistory: shop.capitalHistory?.filter((h) => h.id !== id),
    });
  };

  const handleAddPayment = (payment) => {
    onUpdateShop({
      ...shop,
      saasPayments: [...(shop.saasPayments || []), payment],
    });
    showToast("Payment recorded successfully");
    setShowAddPayment(false);
  };

  const handleDeletePayment = (id) => {
    onUpdateShop({
      ...shop,
      saasPayments: shop.saasPayments?.filter((p) => p.id !== id),
    });
  };

  const tabItems = [
    {
      value: "shop-info",
      icon: faStore,
      label: "Shop Info",
      permission: ["manage_shop", "read_shop", "manage_shop_modules"],
    },
    {
      value: "capital",
      icon: faMoneyBillWave,
      label: "Capital",
      permission: ["manage_capital", "read_capital", "manage_shop_modules"],
    },
    {
      value: "stock",
      icon: faBoxes,
      label: "Stock",
      permission: ["manage_stock", "read_stock", "manage_shop_modules"],
    },
    {
      value: "sales",
      icon: faChartLine,
      label: "Sales",
      permission: ["manage_sales", "read_sales", "manage_shop_modules"],
    },
    {
      value: "owners",
      icon: faUserTie,
      label: "Owners",
      check: () =>
        hasPermission(["manage_business_owner", "read_business_owner"]) &&
        hasPermission(["manage_user_account", "read_user_account"]),
    },
    {
      value: "sellers",
      icon: faUsers,
      label: "Sellers",
      check: () =>
        hasPermission(["manage_business_seller", "read_business_seller"]) &&
        hasPermission(["manage_user_account", "read_user_account"]),
    },
    {
      value: "financial",
      icon: faMoneyBillWave,
      label: "Financial details",
      permission: [
        "manage_financial_records",
        "read_financial_records",
        "manage_shop_modules",
      ],
    },
    {
      value: "closeouts",
      icon: faCalculator,
      label: "Sales Closeouts",
      permission: [
        "manage_sales_closeout",
        "read_sales_closeout",
        "manage_shop_modules",
      ],
    },
    {
      value: "sales-analysis",
      icon: faChartLine,
      label: "Sales Analysis",
    },
    {
      value: "overall-sales",
      icon: faChartBar,
      label: "Overall Sales",
    },
    {
      value: "payment",
      icon: faCreditCard,
      label: "Payment",
      permission: ["manage_payment", "read_payment"],
    },
    {
      value: "notifications",
      icon: faCog,
      label: "Notification Settings",
      permission: ["manage_notification", "read_notification"],
    },
  ];

  const visibleTabs = tabItems.filter((tab) => {
    if (tab.check) return tab.check();
    if (tab.permission) return hasPermission(tab.permission);
    return true;
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <Button
          onClick={onBack}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Shops
        </Button>
      </div>

      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              {shop.name}
            </h2>
            <p className="text-sm text-gray-600">
              {shop.location} · {shop.type}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={statusStyles[shop.status]}>
              {shop.status}
            </Badge>
          </div>
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <Tabs defaultValue="shop-info" className="w-full">
          <div className="border-b border-gray-200 px-4 sm:px-6 pt-4">
            <TabsList className="bg-transparent h-auto p-0 gap-1 w-full flex flex-wrap">
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="py-2 px-3 text-xs sm:text-sm whitespace-nowrap data-[state=active]:bg-blue-800 data-[state=active]:text-white data-[state=active]:shadow-sm border border-gray-200 data-[state=active]:border-blue-800 rounded-md"
                >
                  <FontAwesomeIcon icon={tab.icon} className="mr-1.5 h-3 w-3" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">
                    {tab.label.replace(/\s*\(.*?\)\s*/, "").trim()}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="p-4 sm:p-6">
            <TabsContent value="shop-info">
              <ShopInfoTab shop={shop} transactions={transactions} />
            </TabsContent>

            <TabsContent value="owners">
              <OwnersTab
                shop={shop}
                shopId={shop?.shopId}
                onViewOwner={setViewOwner}
                onEditOwner={setEditOwner}
                onRemoveOwner={handleRemoveOwner}
              />
            </TabsContent>

            <TabsContent value="sellers">
              <SellersTab
                shop={shop}
                onViewSeller={setViewSeller}
                onEditSeller={setEditSeller}
                onRemoveSeller={handleRemoveSeller}
              />
            </TabsContent>

            <TabsContent value="capital">
              <CapitalTab
                shop={shop}
                onAddCapital={() => setShowAddCapital(true)}
                onEditCapital={setEditCapital}
                onDeleteCapital={handleDeleteCapital}
              />
            </TabsContent>

            <TabsContent value="sales-analysis">
              <SalesAnalysisTab />
            </TabsContent>

            <TabsContent value="stock">
              <StockTab
                shop={shop}
                onUpdateShop={onUpdateShop}
                showToast={showToast}
              />
            </TabsContent>

            <TabsContent value="closeouts">
              <SalesCloseoutTab shop={shop} />
            </TabsContent>

            <TabsContent value="financial">
              <FinancialTab shopId={shop.shopId} />
            </TabsContent>

            <TabsContent value="sales">
              <SalesTab shop={shop} />
            </TabsContent>

            <TabsContent value="overall-sales">
              <OverallSalesTab shop={shop} />
            </TabsContent>

            <TabsContent value="payment">
              <PaymentTab
                shop={shop}
                onAddPayment={() => setShowAddPayment(true)}
                onViewPayment={setViewPayment}
                onDeletePayment={handleDeletePayment}
              />
            </TabsContent>

            <TabsContent value="notifications">
              <NotificationSettingsTab
                shop={shop}
                onUpdateShop={onUpdateShop}
                showToast={showToast}
              />
            </TabsContent>
          </div>
        </Tabs>
      </Card>

      {editOwner && (
        <EditOwnerModal
          owner={editOwner}
          onUpdate={handleUpdateOwner}
          onClose={() => setEditOwner(null)}
        />
      )}

      {viewOwner && (
        <ViewOwnerModal owner={viewOwner} onClose={() => setViewOwner(null)} />
      )}

      {editSeller && (
        <EditSellerModal
          seller={editSeller}
          onUpdate={handleUpdateSeller}
          onClose={() => setEditSeller(null)}
        />
      )}

      {viewSeller && (
        <ViewSellerModal
          seller={viewSeller}
          onClose={() => setViewSeller(null)}
        />
      )}

      {showAddCapital && (
        <AddCapitalModal
          onAdd={handleAddCapital}
          onClose={() => setShowAddCapital(false)}
        />
      )}

      {editCapital && (
        <EditCapitalModal
          capitalEntry={editCapital}
          onUpdate={handleEditCapital}
          onClose={() => setEditCapital(null)}
        />
      )}

      {showAddPayment && (
        <AddSaasPaymentModal
          shop={shop}
          onAdd={handleAddPayment}
          onClose={() => setShowAddPayment(false)}
        />
      )}

      {viewPayment && (
        <ViewSaasPaymentModal
          payment={viewPayment}
          onClose={() => setViewPayment(null)}
        />
      )}
    </div>
  );
};

export default ShopDetailView;
